import { Reducer } from 'redux';
import { cloneDeep, isNil } from 'lodash-es';
import dayjs from 'dayjs';
import { TemplateScope, PreviewPageType, FormTypeEnum, FormPayload, FormSettingsEnum } from '@dev/ee-api/ee-api-models';
import { EditorTemplateStatus } from '@dev/template-editor';
import {
    TemplateLoadSuccessAction,
    SetDefaultTemplateAction,
    EditorMetaData,
    LandingPageCreatorState,
    TemplateUploadFileSuccessAction,
    LandingPageCreatorActionTypes,
    SetIsTemplateOpenAction,
    OpenUpsellingOverlayAction,
    ModifyLandingPageAction,
    LandingPageCreatorStatus,
    LoadSuccessAction,
    SaveSuccessAction,
    AddContactListErrorStatusAction,
    SetTemplatePreviewAction,
    LandingPageCreatorTemplate,
    StartEditStepAction,
    SetLandingPageErrorsAction,
    TemplateFormAddNewAction,
    TemplateFormUpdateSavedAction,
    TemplateFormAddLoadedAction,
    TemplateFormsRemoveAction,
    TemplateFormDuplicatedAction,
    TemplateFormPayloadUpdateAction,
    TemplateFormSetMailingPreferencesLabelAction,
    TemplateFormsSetInvalidAction,
    TemplateFormSetErrorsAction,
    ModifyLandingPageMetaDataAction,
    LandingPageMetaData,
    TemplateFormsSetDefaultDoubleOptInDisabledAction,
    LoadPageTemplateSuccessAction,
    TemplateSetMultiSignUpEditMessageAction,
    UpdateTemplateSaveSettingsAction
} from './types';

const generateName = (name?: string): string =>
    `${name ?? 'LandingPage template form'}: ${dayjs().format('YYYY-MM-DD HH:mm:ss')}`;

const getDefaultTemplateState = (): LandingPageCreatorTemplate => ({
    status: EditorTemplateStatus.DEFAULT,
    isNew: true,
    files: {
        filesList: [],
        isFileUploading: false,
    },
    autosaveCounter: 0,
    recentColors: [],
    forms: [],
    isDoubleOptInDisabledByDefault: true,
});

const getDefaultTemplateFormPayload = (blockClientId: string, isDoubleOptInDisabled: boolean, formPayload?: Partial<FormPayload>): Partial<FormPayload> => {
    const payloadFormSettings = formPayload?.FormSettings || [];
    return {
        Name: generateName(blockClientId),
        Html: '',
        Css: '',
        FormType: FormTypeEnum.LandingPageForm,
        FormTemplateName: null,
        ...formPayload,
        FormSettings: isDoubleOptInDisabled ? [FormSettingsEnum.DoubleOptInDisabled, ...payloadFormSettings] : [FormSettingsEnum.AddDoubleOptIn, ...payloadFormSettings]
    };
};

const defaultState: LandingPageCreatorState = {
    stepInEdit: null,
    status: LandingPageCreatorStatus.DEFAULT,
    isTemplateOpen: false,
    isTemplateDeselected: false,
    landingPageObject: null,
    landingPagePayload: null,
    templateObject: null,
    templateObjectReplaced: null,
    template: getDefaultTemplateState(),
    templateReplaced: null,
    openUpsellingFeature: null,
    templatePreview: {
        content: null,
        name: null,
        isPublic: null,
    },
    isGalleryOpen: false,
    templateSaveSettings: null
};

const loadStart = (state: LandingPageCreatorState): LandingPageCreatorState => ({
    ...state,
    status: LandingPageCreatorStatus.LOADING
});

const loadSuccess = (state: LandingPageCreatorState, action: LoadSuccessAction): LandingPageCreatorState => {
    const { landingPageObject, templateObject, landingMetaJson } = action.payload;
    const metaData: EditorMetaData | null = templateObject?.EditorMetaData
        ? JSON.parse(Buffer.from(templateObject.EditorMetaData, 'base64').toString('utf8'))
        : null;

    const landingPageMetaDataObject: LandingPageMetaData | null = landingMetaJson
        ? JSON.parse(Buffer.from(landingMetaJson, 'base64').toString('utf8'))
        : null;

    const isTemplatePublic = templateObject?.TemplateScope === TemplateScope.Public;
    return {
        ...state,
        status: LandingPageCreatorStatus.LOADED,
        landingPageObject,
        templateObject,
        landingPageMetaDataObject,

        template: {
            ...state.template,
            loadedJson: metaData?.data?.contentJson,
            isNew: true,
            status: EditorTemplateStatus.LOADED,
        },
        ...(isTemplatePublic && { templateSaveSettings: { isPublic: true }})
    };
}

const loadError = (state: LandingPageCreatorState): LandingPageCreatorState => ({
    ...state,
    status: LandingPageCreatorStatus.LOAD_ERROR
});

const saveStart = (state: LandingPageCreatorState): LandingPageCreatorState => ({
    ...state,
    status: LandingPageCreatorStatus.SAVING,
    template: {
        ...state.template,
        status: EditorTemplateStatus.SAVING
    }
});

const saveSuccess = (state: LandingPageCreatorState, action: SaveSuccessAction): LandingPageCreatorState => ({
    ...state,
    stepInEdit: null,
    status: LandingPageCreatorStatus.SAVED,
    landingPageObject: action.payload.landingPageObject,
    landingPagePayload: null,
    templateObject: action.payload.templateObject,
    templateObjectReplaced: null,
    template: {
        ...state.template,
        status: EditorTemplateStatus.SAVED,
        isNew: false,
        loadedJson: action.payload?.editorSaveData?.contentJson ?? state.template.loadedJson, 
        recentColors: action.payload?.editorSaveData?.recentColors ?? state.template.recentColors,
        formsFromReplacedTemplate: null
    },
    templateReplaced: null,
    landingPageMetaDataPayload: null,
    landingPageMetaDataObject: action.payload.landingPageMetaDataObject,
    isTemplateDeselected: false,
});

const saveError = (state: LandingPageCreatorState): LandingPageCreatorState => ({
    ...state,
    status: LandingPageCreatorStatus.SAVE_ERROR,
    template: {
        ...state.template,
        status: EditorTemplateStatus.SAVE_ERROR
    }
});


const modifyLandingPage = (state: LandingPageCreatorState, action: ModifyLandingPageAction): LandingPageCreatorState => ({
    ...state,
    landingPagePayload: cloneDeep({
        ...state.landingPagePayload,
        ...action.payload.landingPagePayload
    })
});

const modifyLandingPageMetadata = (state: LandingPageCreatorState, action: ModifyLandingPageMetaDataAction): LandingPageCreatorState => ({
    ...state,
    landingPageMetaDataPayload: {
        ...state?.landingPageMetaDataPayload,
        data: {
            ...state.landingPageMetaDataPayload?.data,
            socialPreview: {
                ...state.landingPageMetaDataPayload?.data?.socialPreview,
                ...action.payload.landingPageMetaDataPayload?.data?.socialPreview
            }
        }
    }
});

const startEditStep = (state: LandingPageCreatorState, action: StartEditStepAction): LandingPageCreatorState => ({
    ...state,
    stepInEdit: action.payload.stepInEdit,
    landingPagePayload: null,
    landingPageMetaDataPayload: null,
    isTemplateDeselected: false,
    template: {
        ...state.template,
        ...state.templateReplaced,
        forms: state.isTemplateDeselected ? state.template.formsFromReplacedTemplate : state.template.forms,
        formsFromReplacedTemplate: null
    },
    templateObjectReplaced: null,
    templateObject: state.templateObjectReplaced ?? state.templateObject
});

const cancelEditStep = (state: LandingPageCreatorState): LandingPageCreatorState => ({
    ...state,
    landingPagePayload: null,
    stepInEdit: null,
    isTemplateDeselected: false,
    template: {
        ...state.template,
        ...state.templateReplaced,
        forms: state.isTemplateDeselected ? state.template.formsFromReplacedTemplate : state.template.forms,
        formsFromReplacedTemplate: null
    },
    templateObjectReplaced: null,
    templateObject: state.templateObjectReplaced ?? state.templateObject,
    landingPageMetaDataPayload: null
});

const templateUploadFileStart = (state: LandingPageCreatorState): LandingPageCreatorState => ({
    ...state,
    template: {
        ...state.template,
        files: {
            ...state.template.files,
            isFileUploading: true,
        }
    }
});

const templateUploadFileSuccess = (
    state: LandingPageCreatorState,
    action: TemplateUploadFileSuccessAction
): LandingPageCreatorState => ({
    ...state,
    template: {
        ...state.template,
        files: {
            filesList: action.payload.filesList,
            isFileUploading: false,
        }
    },
});

const templateUploadFileError = (state: LandingPageCreatorState): LandingPageCreatorState => ({
    ...state,
    template: {
        ...state.template,
        files: {
            ...state.template.files,
            isFileUploading: false,
        },
    }
});


const setDefaultTemplate = (state: LandingPageCreatorState, action: SetDefaultTemplateAction): LandingPageCreatorState => {
    const { templateObject } = action.payload;

    const metaData: EditorMetaData | null = templateObject?.EditorMetaData
        ? JSON.parse(Buffer.from(templateObject.EditorMetaData, 'base64').toString('utf8'))
        : null;

    return {
        ...state,
        // previousTemplate: { ...state.template, status: EditorTemplateStatus.LOADED },
        template: {
            ...state.template,
            loadedJson: metaData?.data?.contentJson,
            isNew: true,
            status: EditorTemplateStatus.LOADED,
        },
        templateReplaced: { ...state.template, status: EditorTemplateStatus.LOADED },
        templateObjectReplaced: state.templateObject,
        isTemplateOpen: true
    };
};

const templateIncrementAutosaveCounter = (state: LandingPageCreatorState): LandingPageCreatorState => ({
    ...state,
    template: {
        ...state.template,
        autosaveCounter: 1 + state.template.autosaveCounter,
    }
});

const templateFormAddNew = (state: LandingPageCreatorState, action: TemplateFormAddNewAction): LandingPageCreatorState => {
    const { blockClientId } = action.payload;
    const exsitngTemplateIndex = state.template.forms?.findIndex((templateForm) => templateForm.blockClientId === blockClientId);
    if (exsitngTemplateIndex > -1) {
        const newTemplateForm = cloneDeep(state.template.forms[exsitngTemplateIndex]);
        newTemplateForm.isDeleted = false;
        const newTemplateForms = [...state.template.forms];
        newTemplateForms.splice(exsitngTemplateIndex, 1, newTemplateForm);
        return {
            ...state,
            template: {
                ...state.template,
                forms: newTemplateForms
            }
        };
    }
    return {
        ...state,
        template: {
            ...state.template,
            forms: [...state.template.forms ?? [], {
                blockClientId,
                isError: false,
                isSaved: false,
                isDeleted: false,
                formPayload: getDefaultTemplateFormPayload(action.payload.blockClientId, state.template.isDoubleOptInDisabledByDefault)
            }]
        }
    };
};

const templateFormDuplicated = (state: LandingPageCreatorState, action: TemplateFormDuplicatedAction): LandingPageCreatorState => {
    const { blockClientId, newBlockClientId } = action.payload;
    const templateToDuplicate = state.template.forms?.find((templateForm) => templateForm.blockClientId === blockClientId);
    if (!templateToDuplicate) {
        return state;
    }
    const newFormName = generateName(newBlockClientId);
    const templateForm = cloneDeep(templateToDuplicate);
    templateForm.blockClientId = newBlockClientId;
    templateForm.formPayload.Name = newFormName
    templateForm.form = null;
    return {
        ...state,
        template: {
            ...state.template,
            forms: [
                ...state.template.forms,
                templateForm
            ]
        }
    };
};

const templateFormPayloadUpdate = (state: LandingPageCreatorState, action: TemplateFormPayloadUpdateAction): LandingPageCreatorState => {
    const { blockClientId, formPayload } = action.payload;
    const templateToUpdateIndex = state.template.forms?.findIndex((templateForm) => templateForm.blockClientId === blockClientId);
    if (templateToUpdateIndex === -1) {
        return {
            ...state,
            template: {
                ...state.template,
                forms: [
                    ...state.template.forms,
                    {
                        blockClientId,
                        form: null,
                        formPayload: getDefaultTemplateFormPayload(blockClientId, state.template.isDoubleOptInDisabledByDefault, formPayload),
                        isError: false,
                        isSaved: false,
                        isDeleted: false,
                    }
                ]
            }
        };
    }
    const newTemplateForm = cloneDeep(state.template.forms[templateToUpdateIndex]);
    newTemplateForm.isDeleted = false;
    newTemplateForm.formPayload = { ...newTemplateForm.formPayload, ...formPayload };
    const newTemplateForms = [...state.template.forms];
    newTemplateForms.splice(templateToUpdateIndex, 1, newTemplateForm)
    return {
        ...state,
        template: {
            ...state.template,
            forms: newTemplateForms
        }
    };
};

const templateFormSetMailingPreferencesLabel = (state: LandingPageCreatorState, action: TemplateFormSetMailingPreferencesLabelAction): LandingPageCreatorState => {
    const { blockClientId, formMailingPreferencesLabel } = action.payload;
    const templateToUpdateIndex = state.template.forms?.findIndex((templateForm) => templateForm.blockClientId === blockClientId);
    if (templateToUpdateIndex === -1) {//won't happen – just a safeguard
        return state;
    }
    const newTemplateForm = cloneDeep(state.template.forms[templateToUpdateIndex]);
    newTemplateForm.isDeleted = false;
    newTemplateForm.formMailingPreferencesLabel = formMailingPreferencesLabel;
    const newTemplateForms = [...state.template.forms];
    newTemplateForms.splice(templateToUpdateIndex, 1, newTemplateForm)
    return {
        ...state,
        template: {
            ...state.template,
            forms: newTemplateForms
        }
    };
};

const templateFormsRemove = (state: LandingPageCreatorState, action: TemplateFormsRemoveAction): LandingPageCreatorState => ({
    ...state,
    template: {
        ...state.template,
        forms: state.template?.forms?.map((templateForm) => {
            if (action.payload.formsBlockClientIds.includes(templateForm.blockClientId)) {
                return {
                    ...templateForm, 
                    isDeleted: true,
                    form: null
                }
            }
            return templateForm;
        })
    }
});

const TemplateFormsFromReplacedTemplateClear = (state: LandingPageCreatorState): LandingPageCreatorState => ({
    ...state,
    template: {
        ...state.template,
        formsFromReplacedTemplate: null
    }
});

const templateFormsSetInvalid = (state: LandingPageCreatorState, action: TemplateFormsSetInvalidAction): LandingPageCreatorState => ({
    ...state,
    template: {
        ...state.template,
        forms: state.template?.forms?.map((templateForm) => {
            const isError = action.payload.blockClientIds.includes(templateForm.blockClientId);
            const isFirstTimeError = isNil(templateForm.isFirstTimeError) ? true : false;
            return (
            {
                ...templateForm,
                isFirstTimeError: isError ? isFirstTimeError : null,
                isError,
            }
        )})
    }
});

const TemplateFormsSetDefaultDoubleOptInDisabled = (state: LandingPageCreatorState, action: TemplateFormsSetDefaultDoubleOptInDisabledAction): LandingPageCreatorState => ({
    ...state,
    template: {
        ...state.template,
        isDoubleOptInDisabledByDefault: action.payload.isDoubleOptInDisabledByDefault
    }
});

const templateFormSetErrors = (state: LandingPageCreatorState, action: TemplateFormSetErrorsAction): LandingPageCreatorState => {
    const templateForms = cloneDeep(state.template.forms);
    const { blockClientId, errors } = action.payload;
    const templateToUpdate = templateForms.find((templateForm) => templateForm.blockClientId === blockClientId);
    if (!templateToUpdate) {
        return state;
    }
    templateToUpdate.errors = errors;
    return {
        ...state,
        template: {
            ...state.template,
            forms: templateForms
        }
    };
};

const templateFormUpdateSaved = (state: LandingPageCreatorState, action: TemplateFormUpdateSavedAction): LandingPageCreatorState => {
    const templateForms = cloneDeep(state.template.forms);
    const { blockClientId, form } = action.payload;
    const templateToUpdate = templateForms.find((templateForm) => templateForm.blockClientId === blockClientId);
    if (!templateToUpdate) {
        return state;
    }
    templateToUpdate.form = form;
    templateToUpdate.isFirstTimeError = null;
    return {
        ...state,
        template: {
            ...state.template,
            forms: templateForms
        }
    };
};

const templateFormAddLoaded = (state: LandingPageCreatorState, action: TemplateFormAddLoadedAction): LandingPageCreatorState => {
    const { blockClientId, form, formMailingPreferencesLabel } = action.payload;
    const {
        Name,
        Html,
        Css,
        FormType,
        FormTemplateName,
        Audience,
        MergeFields,
        FormScope: Scope,
        FormSettings,
        ActivationTemplateName,
        DoubleOptInPage,
        FormAlreadySubscribedPage,
        FormPageType,
        FormSubmitPage,
        EmailsToNotifyOnAction
    } = form;

    const formPayload: FormPayload = {
        Name,
        Html,
        Css,
        Scope,
        FormType: FormType ?? FormTypeEnum.FormCreator,
        FormTemplateName,
        Audience: Audience?.map((item) => item.PublicListID),
        MergeFields,
        FormSettings,
        ActivationTemplateName,
        DoubleOptInPage,
        FormAlreadySubscribedPage,
        FormPageType,
        FormSubmitPage,
        EmailsToNotifyOnAction,
    };

    return {
        ...state,
        template: {
            ...state.template,
            forms: [...state.template.forms ?? [], {
                blockClientId,
                isError: false,
                isSaved: true,
                isDeleted: false,
                form,
                formPayload,
                formMailingPreferencesLabel
            }]
        }
    };
};

const templateChanged = (state: LandingPageCreatorState): LandingPageCreatorState => ({
    ...state,
    template: {
        ...state.template,
        status: EditorTemplateStatus.MODIFIED,
    }
});

const templateDeselect = (state: LandingPageCreatorState): LandingPageCreatorState => ({
        ...state,
        isTemplateDeselected: true,
        template: {
            ...state.template,
            formsFromReplacedTemplate: state.template.forms,
            forms: []
        }
    }
);

// const templateSaveStart = (state: LandingPageCreatorState, action: TemplateSaveStartAction): LandingPageCreatorState => {
//     const { errors, data } = action.payload;
//     const { recentColors } = data;
//     return {
//         ...state,
//         template: {
//             ...state.template,
//             status: EditorTemplateStatus.SAVING,
//             recentColors,
//         }
//     };
// };

// const templateSaveSuccess = (state: LandingPageCreatorState, action: TemplateSaveSuccessAction): LandingPageCreatorState => ({
//     ...state,
//     template: {
//         ...state.template,
//         isNew: false,
//         status: EditorTemplateStatus.SAVED,
//         loadedJson: action.payload.data.contentJson,
//         recentColors: action.payload.data.recentColors
//     },
//     isTemplateDeselected: false,
//     // previousTemplate: null
// });

// const templateSaveError = (state: LandingPageCreatorState, action: TemplateSaveErrorAction): LandingPageCreatorState => ({
//     ...state,
//     template: {
//         ...state.template,
//         status: EditorTemplateStatus.SAVE_ERROR,
//     }
// });

const templateLoadStart = (state: LandingPageCreatorState): LandingPageCreatorState => ({
    ...state,
    template: {
        ...state.template,
        status: EditorTemplateStatus.LOADING,
    }
});

const templateLoadSuccess = (state: LandingPageCreatorState, action: TemplateLoadSuccessAction): LandingPageCreatorState => {
    const { templateObject, useDraftScope } = action.payload;
    const forceDraftScope = useDraftScope && templateObject.TemplateScope !== TemplateScope.Draft;
    
    const metaData: EditorMetaData | null = templateObject.EditorMetaData
        ? JSON.parse(Buffer.from(templateObject.EditorMetaData, 'base64').toString('utf8'))
        : null;

    const template: LandingPageCreatorTemplate = {
        ...state.template,
        loadedJson: (metaData as EditorMetaData)?.data?.contentJson ?? null,
        recentColors: (metaData as EditorMetaData)?.data?.recentColors ?? null,
        isNew: templateObject.IsPublic || forceDraftScope,
        status: EditorTemplateStatus.LOADED,
    };

    if (action.payload.isPublic) {
        return {
            ...state,
            templateReplaced: { ...state.template, status: EditorTemplateStatus.LOADED },
            templateObjectReplaced: state.templateObject,
            template
        };
    }

    return {
        ...state,
        template
    };
};

const templateLoadError = (state: LandingPageCreatorState): LandingPageCreatorState => ({
    ...state,
    template: {
        ...state.template,
        status: EditorTemplateStatus.LOAD_ERROR,
    }
});

const templateSetMultiSignUpEditMessage = (state: LandingPageCreatorState, action: TemplateSetMultiSignUpEditMessageAction): LandingPageCreatorState => ({
    ...state,
    template: {
        ...state.template,
        multiSignUpEditMessage: action.payload.editMessage
    }
});

const setIsTemplateOpen = (state: LandingPageCreatorState, action: SetIsTemplateOpenAction): LandingPageCreatorState => {
    if (action.payload.isOpen) {
        return {
            ...state,
            isTemplateOpen: true
        }
    }
    
    return {
        ...state,
        isTemplateOpen: false,
        template: state?.template
        // template: state.previousTemplate ?? state.template, //restore template if default or from gallery closed unchanged
        // previousTemplate: null,
    };
}

const openUpsellingOverlay = (state: LandingPageCreatorState, action: OpenUpsellingOverlayAction): LandingPageCreatorState => ({
    ...state,
    openUpsellingFeature: action.payload.feature,
});

const setTemplatePreview = (state: LandingPageCreatorState, action: SetTemplatePreviewAction): LandingPageCreatorState => ({
    ...state,
    templatePreview: {
        content: action.payload.content,
        name: action.payload.name,
        isPublic: action.payload.isPublic,
    },
});

const closeUpsellingOverlay = (state: LandingPageCreatorState): LandingPageCreatorState => ({
    ...state,
    openUpsellingFeature: null,
});

const openGallery = (state: LandingPageCreatorState): LandingPageCreatorState => ({
    ...state,
    isGalleryOpen: true,
});

const closeGallery = (state: LandingPageCreatorState): LandingPageCreatorState => ({
    ...state,
    isGalleryOpen: false,
});

const setLandingPageErrors = (state: LandingPageCreatorState, action: SetLandingPageErrorsAction): LandingPageCreatorState => ({
    ...state,
    errors: action.payload.errors,
    status: LandingPageCreatorStatus.SAVE_ERROR
});

const addContactListErrorStatusChange = (state: LandingPageCreatorState, action: AddContactListErrorStatusAction): LandingPageCreatorState => ({
    ...state,
    isAddListError: action.payload.isAddListError
});

const updateTemplateSaveSettings = (state: LandingPageCreatorState, action: UpdateTemplateSaveSettingsAction): LandingPageCreatorState => ({
    ...state,
    templateSaveSettings: {
        ...state.templateSaveSettings,
        ...action.payload
    }
});

const pageTemplateSuccess = (state: LandingPageCreatorState, action: LoadPageTemplateSuccessAction): LandingPageCreatorState => {
    const { pageTemplateType, pageTemplate } = action.payload;
    const newParseTemplates = {
        ...(pageTemplateType === PreviewPageType.ActivationViaEmail && {activationViaEmailPage: pageTemplate}),
        ...(pageTemplateType === PreviewPageType.AlreadySubscribed && {alreadySubscribedPage: pageTemplate}),
        ...(pageTemplateType === PreviewPageType.FormSubmit && {formSubmitPage: pageTemplate}),
    }

    return {
        ...state,
        pageTemplates: {
            ...state.pageTemplates,
            ...newParseTemplates
        }
    }
};

const reducer: Reducer<LandingPageCreatorState> = (
    state: LandingPageCreatorState = defaultState,
    action
): LandingPageCreatorState => {
    switch (action.type) { // eslint-disable-line sonarjs/max-switch-cases
        case LandingPageCreatorActionTypes.LOAD_START:
            return loadStart(state);
        case LandingPageCreatorActionTypes.LOAD_SUCCESS:
            return loadSuccess(state, action as LoadSuccessAction);
        case LandingPageCreatorActionTypes.LOAD_ERROR:
            return loadError(state);
        case LandingPageCreatorActionTypes.SAVE_START:
            return saveStart(state);
        case LandingPageCreatorActionTypes.SAVE_SUCCESS:
            return saveSuccess(state, action as SaveSuccessAction);
        case LandingPageCreatorActionTypes.SAVE_ERROR:
            return saveError(state);
        case LandingPageCreatorActionTypes.MODIFY_LANDING_PAGE:
            return modifyLandingPage(state, action as ModifyLandingPageAction);
        case LandingPageCreatorActionTypes.MODIFY_LANDING_PAGE_METADATA:
            return modifyLandingPageMetadata(state, action as ModifyLandingPageMetaDataAction);
        case LandingPageCreatorActionTypes.START_EDIT_STEP:
            return startEditStep(state, action as StartEditStepAction);
        case LandingPageCreatorActionTypes.CANCEL_EDIT_STEP:
            return cancelEditStep(state);
        case LandingPageCreatorActionTypes.TEMPLATE_UPLOAD_FILE_START:
            return templateUploadFileStart(state);
        case LandingPageCreatorActionTypes.TEMPLATE_UPLOAD_FILE_SUCCESS:
            return templateUploadFileSuccess(state, action as TemplateUploadFileSuccessAction);
        case LandingPageCreatorActionTypes.TEMPLATE_UPLOAD_FILE_ERROR:
            return templateUploadFileError(state);
        case LandingPageCreatorActionTypes.SET_DEFAULT_TEMPLATE:
            return setDefaultTemplate(state, action as SetDefaultTemplateAction);
        case LandingPageCreatorActionTypes.TEMPLATE_INCREMENT_AUTOSAVE_COUNTER:
            return templateIncrementAutosaveCounter(state);
        case LandingPageCreatorActionTypes.TEMPLATE_FORM_ADD_NEW:
            return templateFormAddNew(state, action as TemplateFormAddNewAction);
        case LandingPageCreatorActionTypes.TEMPLATE_FORMS_REMOVE:
            return templateFormsRemove(state, action as TemplateFormsRemoveAction);
        case LandingPageCreatorActionTypes.TEMPLATE_FORMS_FROM_REPLACED_TEMPLATE_CLEAR:
            return TemplateFormsFromReplacedTemplateClear(state);
        case LandingPageCreatorActionTypes.TEMPLATE_FORMS_SET_INVALID:
            return templateFormsSetInvalid(state, action as TemplateFormsSetInvalidAction);
        case LandingPageCreatorActionTypes.TEMPLATE_FORMS_SET_DEFAULT_DOUBLE_OPT_IN:
            return TemplateFormsSetDefaultDoubleOptInDisabled(state, action as TemplateFormsSetDefaultDoubleOptInDisabledAction);
        case LandingPageCreatorActionTypes.TEMPLATE_FORM_SET_ERRORS:
            return templateFormSetErrors(state, action as TemplateFormSetErrorsAction);
        case LandingPageCreatorActionTypes.TEMPLATE_FORM_UPDATE_SAVED:
            return templateFormUpdateSaved(state, action as TemplateFormUpdateSavedAction);
        case LandingPageCreatorActionTypes.TEMPLATE_FORM_ADD_LOADED:
            return templateFormAddLoaded(state, action as TemplateFormAddLoadedAction);
        case LandingPageCreatorActionTypes.TEMPLATE_FORM_SET_MAILING_PREFERENCES_LABEL:
            return templateFormSetMailingPreferencesLabel(state, action as TemplateFormSetMailingPreferencesLabelAction);
        case LandingPageCreatorActionTypes.TEMPLATE_FORM_DUPLICATED:
            return templateFormDuplicated(state, action as TemplateFormDuplicatedAction);
        case LandingPageCreatorActionTypes.TEMPLATE_FORM_PAYLOAD_UPDATE:
            return templateFormPayloadUpdate(state, action as TemplateFormPayloadUpdateAction);
        case LandingPageCreatorActionTypes.TEMPLATE_CHANGED:
            return templateChanged(state);
        case LandingPageCreatorActionTypes.TEMPLATE_DESELECT:
            return templateDeselect(state);
        case LandingPageCreatorActionTypes.TEMPLATE_LOAD_START:
            return templateLoadStart(state);
        case LandingPageCreatorActionTypes.TEMPLATE_LOAD_SUCCESS:
            return templateLoadSuccess(state, action as TemplateLoadSuccessAction);
        case LandingPageCreatorActionTypes.TEMPLATE_LOAD_ERROR:
            return templateLoadError(state);
        case LandingPageCreatorActionTypes.TEMPLATE_SET_MUTLI_SIGN_UP_EDIT_MESSAGE:
            return templateSetMultiSignUpEditMessage(state, action as TemplateSetMultiSignUpEditMessageAction);
        case LandingPageCreatorActionTypes.SET_IS_TEMPLATE_OPEN:
            return setIsTemplateOpen(state, action as SetIsTemplateOpenAction);
        case LandingPageCreatorActionTypes.SET_TEMPLATE_PREVIEW:
            return setTemplatePreview(state, action as SetTemplatePreviewAction);
        case LandingPageCreatorActionTypes.OPEN_UPSELLING_OVERLAY:
            return openUpsellingOverlay(state, action as OpenUpsellingOverlayAction);
        case LandingPageCreatorActionTypes.CLOSE_UPSELLING_OVERLAY:
            return closeUpsellingOverlay(state);
        case LandingPageCreatorActionTypes.OPEN_GALLERY:
            return openGallery(state);
        case LandingPageCreatorActionTypes.CLOSE_GALLERY:
            return closeGallery(state);
        case LandingPageCreatorActionTypes.SET_LANDING_PAGE_ERRORS:
            return setLandingPageErrors(state, action as SetLandingPageErrorsAction);
        case LandingPageCreatorActionTypes.ADD_CONTACT_LIST_ERROR_STATUS:
            return addContactListErrorStatusChange(state, action as AddContactListErrorStatusAction);
        case LandingPageCreatorActionTypes.UPDATE_TEMPLATE_SAVE_SETTINGS:
            return updateTemplateSaveSettings(state, action as UpdateTemplateSaveSettingsAction);
        case LandingPageCreatorActionTypes.RESET:
            return defaultState;
        case LandingPageCreatorActionTypes.LOAD_PAGE_TEMPLATE_SUCCESS:
            return pageTemplateSuccess(state, action as LoadPageTemplateSuccessAction);
        default:
            return state;
    }
};

export { reducer as landingPageCreator };

export default reducer;
